import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getCopaymentLogs, getErrorIncidents, updateErrorIncidentStatus } from '../services/copaymentLogService';

const endpoints = ['', 'sale', 'saleWithPromotion', 'preAuth', 'postAuth', 'cashback', 'refund', 'void', 'reversal', 'verifyTransaction'];
const outcomes = ['', 'APPROVED', 'DECLINED', 'REJECTED', 'NO_ISSUER_RESPONSE', 'TIMEOUT', 'UNREACHABLE', 'UPSTREAM_DROPPED', 'UPSTREAM_4XX', 'UPSTREAM_5XX', 'VALIDATION_REJECTED', 'PANIC'];
const errorSources = ['', 'COPAYMENT', 'ISSUER'];
const errorCategories = ['', 'INTERNAL', 'REQUEST', 'CONFIGURATION', 'DEVICE', 'CARD_DATA', 'ENTRY_MODE', 'EMV', 'PERMISSIONS', 'AVAILABILITY', 'TRANSACTION_STATE', 'ECOMMERCE', 'SECURITY', 'FUNDS'];

const toRFC3339 = (value) => value ? new Date(value).toISOString() : '';
const formatDate = (value) => value ? new Date(value).toLocaleString('es-MX') : '—';
const formatNumber = (value) => new Intl.NumberFormat('es-MX', { maximumFractionDigits: 1 }).format(value || 0);
const formatMoney = (value) => value === '' || value === null || value === undefined
    ? '—'
    : new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(value));
const prettyJSON = (value) => value ? JSON.stringify(value, null, 2) : 'Sin contenido';

const outcomeColor = (outcome) => {
    if (outcome === 'APPROVED') return 'success';
    if (['DECLINED', 'REJECTED', 'NO_ISSUER_RESPONSE'].includes(outcome)) return 'warning';
    return 'error';
};

const StatCard = ({ label, value, suffix = '' }) => (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 150, flex: '1 1 150px' }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h5" sx={{ mt: 0.5 }}>{value}{suffix}</Typography>
    </Paper>
);

const CopaymentLogs = () => {
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState({});
    const [topErrors, setTopErrors] = useState([]);
    const [filters, setFilters] = useState({ endpoint: '', outcome: '', error_source: '', error_category: '', mapped: '', search: '', amount: '', tip_amount: '', from: '', to: '' });
    const [appliedFilters, setAppliedFilters] = useState(filters);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState(null);
    const [incidents, setIncidents] = useState([]);
    const [incidentStats, setIncidentStats] = useState({});
    const [updatingIncident, setUpdatingIncident] = useState(null);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [data, incidentData] = await Promise.all([getCopaymentLogs({
                page: page + 1,
                page_size: pageSize,
                endpoint: appliedFilters.endpoint,
                outcome: appliedFilters.outcome,
                error_source: appliedFilters.error_source,
                error_category: appliedFilters.error_category,
                mapped: appliedFilters.mapped,
                search: appliedFilters.search,
                amount: appliedFilters.amount,
                tip_amount: appliedFilters.tip_amount,
                from: toRFC3339(appliedFilters.from),
                to: toRFC3339(appliedFilters.to)
            }), getErrorIncidents({ page: 1, page_size: 50 })]);
            setItems(data.items || []);
            setStats(data.stats || {});
            setTopErrors(data.top_errors || []);
            setIncidents((incidentData.items || []).filter((item) => item.status !== 'RESOLVED'));
            setIncidentStats(incidentData.stats || {});
        } catch (err) {
            setError(err.response?.status === 403
                ? 'Esta sección está disponible únicamente para administradores.'
                : 'No fue posible consultar los logs de Copayment.');
        } finally {
            setLoading(false);
        }
    }, [appliedFilters, page, pageSize]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const applyFilters = () => {
        setPage(0);
        setAppliedFilters(filters);
    };

    const clearFilters = () => {
        const empty = { endpoint: '', outcome: '', error_source: '', error_category: '', mapped: '', search: '', amount: '', tip_amount: '', from: '', to: '' };
        setFilters(empty);
        setAppliedFilters(empty);
        setPage(0);
    };

    const changeIncidentStatus = async (id, status) => {
        setUpdatingIncident(id);
        setError('');
        try {
            await updateErrorIncidentStatus(id, status);
            await fetchLogs();
        } catch (err) {
            setError('No fue posible actualizar el incidente.');
        } finally {
            setUpdatingIncident(null);
        }
    };

    return (
        <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Box>
                    <Typography variant="h4" component="h1">Monitoreo Copayment</Typography>
                    <Typography color="text.secondary">Solicitudes y respuestas sanitizadas; no se almacenan credenciales ni datos completos de tarjeta.</Typography>
                </Box>
                <Button startIcon={<RefreshIcon />} onClick={fetchLogs} disabled={loading}>Actualizar</Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <StatCard label="Interacciones" value={stats.total || 0} />
                <StatCard label="Aprobadas" value={stats.approved || 0} />
                <StatCard label="Rechazos de negocio" value={stats.business_declined || 0} />
                <StatCard label="Errores técnicos" value={stats.technical_errors || 0} />
                <StatCard label="No mapeados" value={stats.unmapped_errors || 0} />
                <StatCard label="Reintentables" value={stats.retryable_errors || 0} />
                <StatCard label="Latencia promedio" value={formatNumber(stats.average_latency_ms)} suffix=" ms" />
                <StatCard label="Latencia p95" value={formatNumber(stats.p95_latency_ms)} suffix=" ms" />
            </Box>

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 2 }}>
                    <Box>
                        <Typography variant="h6">Incidentes operativos activos</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Agrupa fallas internas, de disponibilidad y códigos aún no mapeados. Cada transacción permanece en el historial.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Chip color="error" label={`Abiertos: ${incidentStats.open || 0}`} />
                        <Chip color="warning" variant="outlined" label={`Reconocidos: ${incidentStats.acknowledged || 0}`} />
                    </Stack>
                </Box>
                {incidents.length === 0 ? (
                    <Typography color="text.secondary">No hay incidentes activos.</Typography>
                ) : (
                    <Stack spacing={1}>
                        {incidents.map((incident) => (
                            <Box key={incident.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, flexWrap: 'wrap' }}>
                                <Chip size="small" color={incident.status === 'OPEN' ? 'error' : 'warning'} label={incident.status === 'OPEN' ? 'Abierto' : 'Reconocido'} />
                                <Box sx={{ flex: '1 1 360px' }}>
                                    <Typography variant="subtitle2">{incident.canonical_error_code} · {incident.endpoint}</Typography>
                                    <Typography variant="body2">{incident.message_es}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {incident.occurrence_count} ocurrencia(s) · última {formatDate(incident.last_seen_at)} · UUID {incident.last_uuid || '—'}
                                    </Typography>
                                </Box>
                                {incident.status === 'OPEN' && (
                                    <Button size="small" disabled={updatingIncident === incident.id} onClick={() => changeIncidentStatus(incident.id, 'ACKNOWLEDGED')}>Reconocer</Button>
                                )}
                                <Button size="small" variant="contained" color="success" disabled={updatingIncident === incident.id} onClick={() => changeIncidentStatus(incident.id, 'RESOLVED')}>Resolver</Button>
                            </Box>
                        ))}
                    </Stack>
                )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField select size="small" label="Endpoint" value={filters.endpoint} onChange={(e) => setFilters({ ...filters, endpoint: e.target.value })} sx={{ minWidth: 180 }}>
                        {endpoints.map((value) => <MenuItem key={value || 'all'} value={value}>{value || 'Todos'}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label="Resultado" value={filters.outcome} onChange={(e) => setFilters({ ...filters, outcome: e.target.value })} sx={{ minWidth: 210 }}>
                        {outcomes.map((value) => <MenuItem key={value || 'all'} value={value}>{value || 'Todos'}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label="Origen del error" value={filters.error_source} onChange={(e) => setFilters({ ...filters, error_source: e.target.value })} sx={{ minWidth: 180 }}>
                        {errorSources.map((value) => <MenuItem key={value || 'all'} value={value}>{value || 'Todos'}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label="Categoría" value={filters.error_category} onChange={(e) => setFilters({ ...filters, error_category: e.target.value })} sx={{ minWidth: 210 }}>
                        {errorCategories.map((value) => <MenuItem key={value || 'all'} value={value}>{value || 'Todas'}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label="Mapeo" value={filters.mapped} onChange={(e) => setFilters({ ...filters, mapped: e.target.value })} sx={{ minWidth: 160 }}>
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="true">Mapeados</MenuItem>
                        <MenuItem value="false">No mapeados</MenuItem>
                    </TextField>
                    <TextField size="small" label="Buscar UUID, dispositivo, adquirente o error" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} sx={{ minWidth: 300, flexGrow: 1 }} />
                    <TextField size="small" label="Monto exacto" placeholder="100.00" value={filters.amount} onChange={(e) => setFilters({ ...filters, amount: e.target.value })} sx={{ width: 150 }} />
                    <TextField size="small" label="Propina exacta" placeholder="15.00" value={filters.tip_amount} onChange={(e) => setFilters({ ...filters, tip_amount: e.target.value })} sx={{ width: 150 }} />
                    <TextField size="small" label="Desde" type="datetime-local" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} InputLabelProps={{ shrink: true }} />
                    <TextField size="small" label="Hasta" type="datetime-local" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} InputLabelProps={{ shrink: true }} />
                    <Button variant="contained" onClick={applyFilters}>Aplicar</Button>
                    <Button onClick={clearFilters}>Limpiar</Button>
                </Box>
            </Paper>

            {topErrors.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>Errores más frecuentes</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {topErrors.map((item) => (
                            <Chip
                                key={`${item.source}-${item.code}-${item.provider_code || ''}`}
                                color={item.code.endsWith('_UNMAPPED') ? 'error' : 'warning'}
                                variant="outlined"
                                label={`${item.code}${item.provider_code ? ` (${item.provider_code})` : ''} · ${item.count}`}
                                title={item.message}
                            />
                        ))}
                    </Box>
                </Paper>
            )}

            {error && <Alert severity="error">{error}</Alert>}
            <Paper variant="outlined">
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Fecha</TableCell>
                                        <TableCell>Endpoint</TableCell>
                                        <TableCell>Resultado</TableCell>
                                        <TableCell>HTTP</TableCell>
                                        <TableCell>Latencia</TableCell>
                                        <TableCell>Dispositivo / UUID</TableCell>
                                        <TableCell align="right">Monto</TableCell>
                                        <TableCell align="right">Propina</TableCell>
                                        <TableCell>ID adquirente</TableCell>
                                        <TableCell>Error normalizado</TableCell>
                                        <TableCell align="right">Detalle</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.map((item) => (
                                        <TableRow key={item.id} hover>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(item.created_at)}</TableCell>
                                            <TableCell>{item.endpoint}</TableCell>
                                            <TableCell><Chip size="small" label={item.outcome} color={outcomeColor(item.outcome)} variant="outlined" /></TableCell>
                                            <TableCell>{item.http_status || '—'}</TableCell>
                                            <TableCell>{item.latency_ms ?? 0} ms</TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{item.device_id || '—'}</Typography>
                                                <Typography variant="caption" color="text.secondary">{item.uuid || '—'}</Typography>
                                            </TableCell>
                                            <TableCell align="right">{formatMoney(item.amount)}</TableCell>
                                            <TableCell align="right">{formatMoney(item.tip_amount)}</TableCell>
                                            <TableCell>{item.acquirer_transaction_id || item.ref_acquirer_transaction_id || '—'}</TableCell>
                                            <TableCell sx={{ minWidth: 220 }}>
                                                {item.canonical_error_code ? (
                                                    <>
                                                        <Chip
                                                            size="small"
                                                            color={item.error_mapped === false ? 'error' : 'warning'}
                                                            label={item.canonical_error_code}
                                                            variant="outlined"
                                                        />
                                                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{item.error_message_es}</Typography>
                                                    </>
                                                ) : '—'}
                                            </TableCell>
                                            <TableCell align="right"><Button size="small" onClick={() => setSelected(item)}>Ver</Button></TableCell>
                                        </TableRow>
                                    ))}
                                    {items.length === 0 && (
                                        <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4 }}>No hay registros para estos filtros.</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={stats.total || 0}
                            page={page}
                            onPageChange={(_, nextPage) => setPage(nextPage)}
                            rowsPerPage={pageSize}
                            onRowsPerPageChange={(event) => { setPageSize(Number(event.target.value)); setPage(0); }}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            labelRowsPerPage="Registros por página"
                        />
                    </>
                )}
            </Paper>

            <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="lg" fullWidth>
                <DialogTitle>Detalle de interacción con Copayment</DialogTitle>
                <DialogContent dividers>
                    {selected && (
                        <Stack spacing={2}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip label={selected.endpoint} />
                                <Chip label={selected.outcome} color={outcomeColor(selected.outcome)} />
                                <Chip label={`HTTP ${selected.http_status || 'sin respuesta'}`} />
                                <Chip label={`${selected.latency_ms || 0} ms`} />
                                <Chip label={`Publicado: ${selected.published === true ? 'sí' : selected.published === false ? 'no' : 'N/A'}`} />
                            </Box>
                            {selected.error_message && <Alert severity="error">{selected.error_message}</Alert>}
                            {selected.canonical_error_code && (
                                <Alert severity={selected.error_mapped === false ? 'error' : 'warning'}>
                                    <Typography variant="subtitle2">{selected.canonical_error_code} · {selected.error_category}</Typography>
                                    <Typography variant="body2">{selected.error_message_es}</Typography>
                                    {selected.operator_action && <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{selected.operator_action}</Typography>}
                                </Alert>
                            )}
                            <Box>
                                <Typography variant="subtitle1">Solicitud sanitizada</Typography>
                                <Box component="pre" sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, overflow: 'auto', maxHeight: 360, whiteSpace: 'pre-wrap' }}>{prettyJSON(selected.request_body)}</Box>
                            </Box>
                            <Box>
                                <Typography variant="subtitle1">Respuesta sanitizada</Typography>
                                <Box component="pre" sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, overflow: 'auto', maxHeight: 360, whiteSpace: 'pre-wrap' }}>{prettyJSON(selected.response_body)}</Box>
                            </Box>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions><Button onClick={() => setSelected(null)}>Cerrar</Button></DialogActions>
            </Dialog>
        </Stack>
    );
};

export default CopaymentLogs;
